import { AbstractDialog } from "../abstract_dialog.js";
import { DialogManager } from "../dialog_manager.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { DialogId } from "../dialog_id.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { LinkedSocialProvider } from "../account_providers_preview_state.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";

export class ConnectAccountDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.CONNECT_ACCOUNT_DIALOG;

  constructor( private readonly provider: LinkedSocialProvider, private readonly on_confirm?: () => void )
  {
    const design_body_w = 910;
    const design_body_h = 652;

    const body_w = LayoutScaler.widthFromDesignAdaptive( design_body_w );
    const body_h = LayoutScaler.heightFromDesign( design_body_h );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground();
    this.createTopBgPart();
    this.createTitle();
    this.createText();
    this.createActionButtons();
    this.createCloseButton();
  }

  private getProviderIcon(): ImgID
  {
    switch ( this.provider )
    {
      case LinkedSocialProvider.Google:   return ImgID.GoogleIcon;
      case LinkedSocialProvider.Facebook: return ImgID.FacebookIcon;
      case LinkedSocialProvider.Apple:    return ImgID.AppleIcon;
    }
  }

  private getProviderDisplayName(): string
  {
    switch ( this.provider )
    {
      case LinkedSocialProvider.Google:   return "Google";
      case LinkedSocialProvider.Facebook: return "Facebook";
      case LinkedSocialProvider.Apple:    return "Apple";
    }
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( 0 ),
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );
  }

  private createTopBgPart(): void
  {
    const design_w = 237;
    const design_h = 128;
    const design_y = -80;
    const design_icon_size = 134;
    const design_icon_y = 55;

    const part_w = LayoutScaler.widthFromDesign( design_w );
    const part_h = LayoutScaler.heightFromDesign( design_h );
    const part_x = this.rect.getW2() - part_w / 2;
    const part_y = LayoutScaler.yFromDesign( design_y );

    const bg_part = new UIImage(
      this.ctx,
      part_x,
      part_y,
      part_w,
      part_h,
      ImgID.UI_CapsuleTopBgPart,
      {},
      false
    );
    this.addChild( bg_part );

    const icon_w = LayoutScaler.widthFromDesign( design_icon_size );
    const icon_h = LayoutScaler.heightFromDesign( design_icon_size );
    const icon_x = Math.ceil( ( part_w - icon_w ) / 2 );
    const icon_y = LayoutScaler.yFromDesign( design_icon_y );

    const main_provider_icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      this.getProviderIcon()
    );
    bg_part.addChild( main_provider_icon, true );
  }

  private createTitle(): void
  {
    const design_h = 131;
    const design_y = 130;
    const design_font_size = 60;
    const design_outline_w = 20;

    const label_w = this.rect.getW() * 0.8;
    const label_h = LayoutScaler.heightFromDesign( design_h );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_y );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "CONNECT_ACCOUNT_DIALOG.TITLE",
        text_variable_params: [ this.getProviderDisplayName() ],
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.booster_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  private createText(): void
  {
    const design_title_bottom_y = 240;
    const design_btn_y = 479;
    const design_w = 760;
    const design_font_size = 50;
    const design_outline_w = 10;

    const label_w = LayoutScaler.widthFromDesign( design_w );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_title_bottom_y );
    const label_h = LayoutScaler.yFromDesign( design_btn_y ) - label_y;

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "CONNECT_ACCOUNT_DIALOG.TEXT",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  private createActionButtons(): void
  {
    const design_btn_w = 380;
    const design_btn_h = 112;
    const design_gap = 24;
    const design_btn_y = 479;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );
    const gap   = LayoutScaler.widthFromDesign( design_gap );
    const total_w = btn_w * 2 + gap;
    const start_x = this.rect.getW2() - total_w / 2;
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const cancel_btn = new RoundedRectangle(
      this.ctx, start_x, btn_y, btn_w, btn_h, btn_h / 2, "#F2A618", undefined, true
    );
    cancel_btn.addChild( this.actionButtonText( "CANCEL", btn_w, btn_h ), true );
    cancel_btn.setOnClick( () => { this.close(); } );
    this.addChild( cancel_btn );

    const connect_btn = new RoundedRectangle(
      this.ctx, start_x + btn_w + gap, btn_y, btn_w, btn_h, btn_h / 2, "#10B2E2", undefined, true
    );
    connect_btn.addChild( this.actionButtonText( "CONNECT", btn_w, btn_h ), true );
    connect_btn.setOnClick( () => { this.close(); this.on_confirm?.(); } );
    this.addChild( connect_btn );
  }

  private actionButtonText( localization_key: string, btn_w: number, btn_h: number ): UITextElement
  {
    const design_font_size = 47;

    return new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;
    const design_offset_x = 10;
    const design_offset_y = 100;

    const btn_size = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( design_offset_x );
    const btn_y = -LayoutScaler.yFromDesign( design_offset_y );

    const icon = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    icon.setOnClick( () =>
    {
      this.close();
    } );
    this.addChild( icon );
  }

  public name(): string { return "ConnectAccountDialog"; }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
